import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  Portal,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';

import { useAppState } from '../context/LoadTestContext';
import CalibrationDialog from './CalibrationDialog';
import { savedMessage, useCalibrationCommit } from './useCalibrationCommit';

// Everything about calibration that floats over the dashboard: the Calibration
// window, the bar shown while a preview is up (save it, keep editing or drop
// it) and the confirmation after a save. Mounted once per page, beside whichever
// dashboard view is showing, so none of it is lost when the header that holds
// the Calibration button re-lays itself out. The button opens the window
// through the dashboard context (calDialogOpen).
function CalibrationHost() {
  const { calPreview, endCalPreview, calDialogOpen, setCalDialogOpen } = useAppState();
  const { save, apply } = useCalibrationCommit();

  const [message, setMessage] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // Save or apply exactly what is being previewed, straight from the bar.
  const commitPreview = async () => {
    setCommitting(true);
    setPreviewError(null);
    try {
      if (calPreview.dirty) {
        const revision = await save(calPreview.settings, calPreview.note);
        setMessage(savedMessage(revision));
      } else {
        await apply(calPreview.baseRevisionId);
        setMessage(`Rev ${calPreview.baseRevNumber} is now in use`);
      }
    } catch (err) {
      setPreviewError(err.message);
    } finally {
      setCommitting(false);
    }
  };

  const discardPreview = () => {
    setPreviewError(null);
    endCalPreview();
  };

  return (
    <>
      <CalibrationDialog open={calDialogOpen} onClose={() => setCalDialogOpen(false)} onDone={setMessage} />

      {/* Hidden while the window is open: it sits above dialogs. */}
      {calPreview && !calDialogOpen && (
        <Portal>
          <Paper
            role='status'
            elevation={8}
            sx={{
              position: 'fixed',
              left: '50%',
              bottom: 16,
              transform: 'translateX(-50%)',
              zIndex: (theme) => theme.zIndex.snackbar,
              width: 'min(980px, calc(100vw - 32px))',
              p: 1.5,
              borderLeft: 4,
              borderColor: 'warning.main',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box sx={{ flex: '1 1 280px', minWidth: 0 }}>
              <Typography variant='body2' fontWeight={600}>
                {calPreview.dirty
                  ? `Previewing unsaved changes to Rev ${calPreview.baseRevNumber}`
                  : `Previewing Rev ${calPreview.baseRevNumber}`}
              </Typography>
              <Typography variant='caption' color={previewError ? 'error' : 'text.secondary'}>
                {previewError || `Only you see this. Live updates are paused until you ${calPreview.dirty ? 'save' : 'apply'} or discard it.`}
              </Typography>
            </Box>
            <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
              <Button size='small' onClick={discardPreview} disabled={committing}>Discard preview</Button>
              <Button size='small' variant='outlined' onClick={() => setCalDialogOpen(true)} disabled={committing}>
                Keep editing
              </Button>
              <Button size='small' variant='contained' onClick={commitPreview} disabled={committing}>
                {committing
                  ? 'Working…'
                  : calPreview.dirty ? `Save as Rev ${calPreview.nextRev}` : `Apply Rev ${calPreview.baseRevNumber}`}
              </Button>
            </Stack>
          </Paper>
        </Portal>
      )}

      <Snackbar
        open={Boolean(message)}
        autoHideDuration={5000}
        onClose={() => setMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity='success' variant='filled' onClose={() => setMessage(null)}>{message}</Alert>
      </Snackbar>
    </>
  );
}

export default CalibrationHost;
